/**
 * ComparisonFinancials — column-per-site financial metrics grid with three
 * sub-tabs (Acquisition · Operating · ROI Analysis) matching the
 * FinancialsWorkspace structure.
 *
 *   ┌────────────────┬──────────┬──────────┬──────────┐
 *   │  ROI Analysis  │ Site A   │ Site B   │ Site C   │
 *   ├────────────────┼──────────┼──────────┼──────────┤
 *   │ NPV            │ $200K  ★ │ $150K    │ $80K     │
 *   │ IRR            │ 12.5%    │ 14.0% ★  │ 9.2%     │
 *   │ Payback        │ 4.2 yrs  │ 3.8 yrs ★│ 5.1 yrs  │
 *   │ Yield-on-Cost  │ 8.5%   ★ │ 7.9%     │ 6.4%     │
 *   │ Cap Rate       │ 6.2%   ★ │ 5.8%     │ 5.1%     │
 *   └────────────────┴──────────┴──────────┴──────────┘
 *
 * Best-cell highlight (★ in the diagram, blue ring + bold in the UI):
 *   higher is better: NPV, IRR, Yield-on-Cost, Cap Rate, ROI %, Revenue
 *   lower  is better: Payback, Acquisition / Operating costs
 *   ties win together; null values never qualify
 *
 * Data flow:
 *   1. on mount → fetchFinancialsComparison(evalId)
 *   2. for each selected site × {acquisition, operating, roi} where the
 *      compare row exposes a saved model → calculateMetrics(...) so
 *      metricsBySite[siteId][modelType].rollup populates
 *
 * Empty cells (no saved model, or rollup not yet computed) render as "—".
 *
 * Linear: MYD-219 (F4).
 */

import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Tabs, TabsList, TabsTrigger, TabsContent } from 'components/ui/tabs'
import { Card } from 'components/ui/card'
import { DollarSign } from 'lucide-react'
import { cn } from 'lib/utils'

import {
	fetchFinancialsComparison,
	calculateMetrics,
} from 'slices/siteFinancialSlice'
import ComparisonRowLabel from './ComparisonRowLabel'
import ComparisonSiteHeader from './ComparisonSiteHeader'
import { gridTemplate } from './comparisonShared'

type ModelType = 'acquisition' | 'operating' | 'roi'

interface ComparisonFinancialsProps {
	evalId: string
	sites: any[]
}

interface MetricRow {
	key: string
	label: string
	betterWhen: 'higher' | 'lower'
	format: (n: number | null | undefined) => string
}

const fmtCurrency = (n: number | null | undefined) =>
	n == null
		? '—'
		: n.toLocaleString('en-US', {
				style: 'currency',
				currency: 'USD',
				maximumFractionDigits: 0,
			})
const fmtPercent = (n: number | null | undefined) =>
	n == null ? '—' : `${n.toFixed(1)}%`
const fmtYears = (n: number | null | undefined) =>
	n == null ? '—' : `${n.toFixed(1)} yr${n === 1 ? '' : 's'}`
const fmtIRR = (n: number | null | undefined) =>
	n == null ? '—' : `${(n * 100).toFixed(2)}%`

const ROWS_BY_MODEL: Record<ModelType, MetricRow[]> = {
	acquisition: [
		{
			key: 'acquisitionTotal',
			label: 'Acquisition total',
			betterWhen: 'lower',
			format: fmtCurrency,
		},
	],
	operating: [
		{
			key: 'fiveYearOperatingTotal',
			label: '5-yr operating total',
			betterWhen: 'lower',
			format: fmtCurrency,
		},
		{
			key: 'npv',
			label: 'NPV of costs',
			betterWhen: 'lower',
			format: fmtCurrency,
		},
	],
	roi: [
		{ key: 'npv', label: 'NPV', betterWhen: 'higher', format: fmtCurrency },
		{ key: 'irr', label: 'IRR', betterWhen: 'higher', format: fmtIRR },
		{
			key: 'paybackPeriodYears',
			label: 'Payback',
			betterWhen: 'lower',
			format: fmtYears,
		},
		{
			key: 'yieldOnCost',
			label: 'Yield-on-Cost',
			betterWhen: 'higher',
			format: fmtPercent,
		},
		{ key: 'capRate', label: 'Cap rate', betterWhen: 'higher', format: fmtPercent },
		{
			key: 'roiPercent',
			label: 'ROI %',
			betterWhen: 'higher',
			format: fmtPercent,
		},
		{
			key: 'fiveYearRevenueTotal',
			label: '5-yr revenue',
			betterWhen: 'higher',
			format: fmtCurrency,
		},
	],
}

const ComparisonFinancials = ({ evalId, sites }: ComparisonFinancialsProps) => {
	const dispatch = useDispatch()
	const [activeModel, setActiveModel] = useState<ModelType>('roi')

	const comparison: any[] = useSelector(
		(state: any) => state.siteFinancials?.comparisonByEvaluation?.[evalId] || [],
	)
	const metricsBySite: Record<string, Record<string, any>> = useSelector(
		(state: any) => state.siteFinancials?.metricsBySite || {},
	)

	useEffect(() => {
		;(dispatch as any)(fetchFinancialsComparison(evalId))
	}, [dispatch, evalId])

	// Once compare data + selected sites are known, fan out calculateMetrics
	// per (site, modelType) where a saved model exists. We only kick off a
	// fresh calculation when no rollup is present yet (or the existing one
	// belongs to a different lineItems set — we trust the slice's
	// last-rollup-wins behaviour to converge).
	useEffect(() => {
		if (comparison.length === 0 || sites.length === 0) return
		const compareBySiteId: Record<string, any> = {}
		for (const row of comparison) compareBySiteId[String(row.siteId)] = row

		for (const site of sites) {
			const row = compareBySiteId[site._id]
			if (!row) continue
			for (const modelType of Object.keys(row.models || {}) as ModelType[]) {
				const existing = metricsBySite[site._id]?.[modelType]?.rollup
				if (existing) continue
				const m = row.models[modelType]
				;(dispatch as any)(
					calculateMetrics({
						evalId,
						siteId: site._id,
						modelType,
						lineItems: m.lineItems,
						assumptions: m.assumptions,
					}),
				)
			}
		}
		// We deliberately do NOT depend on metricsBySite — the gate above
		// short-circuits when a rollup exists, and depending on it would loop.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [comparison, sites, dispatch, evalId])

	// Build the values matrix for the active model: { [siteId]: { [metricKey]: number | null } }
	const valuesBySiteId = useMemo(() => {
		const out: Record<string, Record<string, number | null>> = {}
		for (const site of sites) {
			const rollup = metricsBySite[site._id]?.[activeModel]?.rollup
			out[site._id] = {}
			for (const row of ROWS_BY_MODEL[activeModel]) {
				const v = rollup?.[row.key]
				out[site._id][row.key] = typeof v === 'number' ? v : null
			}
		}
		return out
	}, [sites, metricsBySite, activeModel])

	// Best-value lookup per row.
	const bestBySiteByKey = useMemo(() => {
		const map: Record<string, Record<string, boolean>> = {}
		for (const site of sites) map[site._id] = {}

		for (const row of ROWS_BY_MODEL[activeModel]) {
			const validEntries = sites
				.map((s) => ({ siteId: s._id, value: valuesBySiteId[s._id][row.key] }))
				.filter(
					(entry): entry is { siteId: string; value: number } =>
						typeof entry.value === 'number' && Number.isFinite(entry.value),
				)
			if (validEntries.length < 2) continue
			const best =
				row.betterWhen === 'higher'
					? Math.max(...validEntries.map((e) => e.value))
					: Math.min(...validEntries.map((e) => e.value))
			for (const e of validEntries) {
				if (Math.abs(e.value - best) < 1e-6) {
					map[e.siteId][row.key] = true
				}
			}
		}
		return map
	}, [sites, valuesBySiteId, activeModel])

	const hasAnyData = useMemo(() => {
		for (const site of sites) {
			const v = valuesBySiteId[site._id]
			for (const row of ROWS_BY_MODEL[activeModel]) {
				if (v?.[row.key] != null) return true
			}
		}
		return false
	}, [sites, valuesBySiteId, activeModel])

	const renderTable = () => {
		const rows = ROWS_BY_MODEL[activeModel]
		return (
			<div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
				<div
					className="grid"
					style={{ gridTemplateColumns: gridTemplate(sites.length), minWidth: '100%' }}
				>
					{/* Header row */}
					<div className="bg-gray-50 border-b border-gray-200 px-3 py-2 sticky left-0 z-10" />
					{sites.map((site) => (
						<ComparisonSiteHeader key={site._id} site={site} />
					))}

					{/* Metric rows */}
					{rows.map((row, idx) => (
						<div className="contents" key={row.key}>
							<ComparisonRowLabel
								label={row.label}
								className={idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white'}
							/>
							{sites.map((site) => {
								const value = valuesBySiteId[site._id][row.key]
								const isBest = bestBySiteByKey[site._id]?.[row.key]
								return (
									<div
										key={site._id}
										className={cn(
											'px-3 py-2 text-sm border-t border-gray-100',
											idx % 2 === 1 ? 'bg-gray-50/60' : 'bg-white',
											isBest && 'bg-[#0080fe]/10',
										)}
									>
										{value == null ? (
											<span className="text-gray-400">—</span>
										) : (
											<span
												className={cn(
													isBest
														? 'font-bold text-[#0080fe]'
														: 'font-medium text-gray-800',
												)}
											>
												{row.format(value)}
												{isBest && (
													<span
														className="ml-1.5 text-[10px] uppercase tracking-wide"
														aria-label="best in row"
													>
														★ best
													</span>
												)}
											</span>
										)}
									</div>
								)
							})}
						</div>
					))}
				</div>
			</div>
		)
	}

	if (!hasAnyData && comparison.length === 0) {
		return (
			<Card className="p-8 text-center">
				<DollarSign className="w-8 h-8 mx-auto text-gray-300 mb-3" />
				<h4 className="text-sm font-semibold text-gray-700 mb-1">
					No financials yet
				</h4>
				<p className="text-xs text-gray-500">
					Add acquisition / operating / ROI line items in each site's Financials tab to
					populate this comparison.
				</p>
			</Card>
		)
	}

	return (
		<div className="space-y-3">
			<Tabs value={activeModel} onValueChange={(v: string) => setActiveModel(v as ModelType)}>
				<TabsList>
					<TabsTrigger value="acquisition">Acquisition</TabsTrigger>
					<TabsTrigger value="operating">Operating</TabsTrigger>
					<TabsTrigger value="roi">ROI Analysis</TabsTrigger>
				</TabsList>
				<TabsContent value="acquisition" className="mt-3">
					{renderTable()}
				</TabsContent>
				<TabsContent value="operating" className="mt-3">
					{renderTable()}
				</TabsContent>
				<TabsContent value="roi" className="mt-3">
					{renderTable()}
				</TabsContent>
			</Tabs>
		</div>
	)
}

export default ComparisonFinancials
